import { useEffect, useRef, useState } from "react";
import isClient from "@mkt/utils/isClient"

const useSlider = ({
  gap,
  itemsPerView,
  itemsPerViewTablet,
  itemsPerViewDesk,
  scrollBy,
  scrollByTablet,
  scrollByDesk,
  numberOfSlides = 0,
}) => {
  const [isLeftBtnDisabled, setIsLeftBtnDisabled] = useState(true);
  const [isRightBtnDisabled, setIsRightBtnDisabled] = useState(false);

  const [usedItemsPerView, setUsedItemsPerView] = useState(() => {
    if (isClient()) {
      if (window.innerWidth < 768) {
        return itemsPerView;
      } else if (window.innerWidth >= 768 && window.innerWidth < 991) {
        return itemsPerViewTablet;
      } else {
        return itemsPerViewDesk;
      }
    }

    return itemsPerView;
  });

  const [usedScrollBy, setUsedScrollBy] = useState(() => {
    if (isClient()) {
      if (window.innerWidth < 768) {
        return scrollBy;
      } else if (window.innerWidth >= 768 && window.innerWidth < 991) {
        return scrollByTablet;
      } else {
        return scrollByDesk;
      }
    }

    return scrollBy;
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(() => {
    return Math.ceil(numberOfSlides/usedItemsPerView)
  });

  const holderRef = useRef(null);
  const trailRef = useRef(null);

  function getScrollByDistance(scrollType) {
    if (scrollType === 'single') return 10;

    const slidesHolderSize = holderRef.current?.clientWidth;
    return slidesHolderSize;
  }

  function handleScrollRight() {
    if (currentPage < numberOfPages) {
      const sliderScrollDistance = getScrollByDistance(usedScrollBy) + gap;
      scrollSliderTo(sliderScrollDistance);
    }
  }

  function handleScrollLeft() {
    if (currentPage > 1) {
      const sliderScrollDistance = getScrollByDistance(usedScrollBy) + gap;
      scrollSliderTo(-sliderScrollDistance);
    }
  }

  function scrollSliderTo(distance) {
    trailRef.current?.scrollBy({ left: distance, behavior: 'smooth' });
  }

  function handleNavigationArrows() {
    const sliderScrollDistance = getScrollByDistance(usedScrollBy) + gap;
    const slidesPosition = trailRef.current?.scrollLeft + trailRef.current?.clientWidth;
    const slideSize = sliderScrollDistance - gap;
    let page = 0;

    if (usedItemsPerView === 1 || (usedItemsPerView%2) === 0 || window.innerWidth < 768) {
      page = Math.floor(slidesPosition/slideSize);
    } else {
      page = Math.ceil(slidesPosition/slideSize);
    }

    setCurrentPage(() => {
      if (page <= numberOfPages) {
        return page;
      }

      return numberOfPages;
    });
  }

  function getUsedItemsPerView() {
    if (window.innerWidth < 768) {
      setUsedItemsPerView(itemsPerView);
      setUsedScrollBy(scrollBy);
      setNumberOfPages(Math.ceil(numberOfSlides/itemsPerView));
    } else if (window.innerWidth >= 768 && window.innerWidth < 991) {
      setUsedItemsPerView(itemsPerViewTablet);
      setUsedScrollBy(scrollByTablet);
      setNumberOfPages(Math.ceil(numberOfSlides/itemsPerViewTablet));
    } else {
      setUsedItemsPerView(itemsPerViewDesk);
      setUsedScrollBy(scrollByDesk);
      setNumberOfPages(Math.ceil(numberOfSlides/itemsPerViewDesk));
    }
  }

  function handleResize() {
    setCurrentPage(1);
    getUsedItemsPerView();

    trailRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    setIsLeftBtnDisabled(() => {
      if (numberOfPages <= 1 || currentPage === 1) {
        return true;
      }

      return false;
    });

    setIsRightBtnDisabled(() => {
      if (numberOfPages <= 1 || currentPage === numberOfPages) {
        return true;
      }

      return false;
    });
  }, [currentPage, numberOfPages]);

  useEffect(() => {
    setNumberOfPages(Math.ceil(numberOfSlides/usedItemsPerView));
  }, [usedItemsPerView]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, []);

  return {
    index: `${currentPage} de ${numberOfPages}`,
    isLeftBtnDisabled,
    isRightBtnDisabled,
    handleScrollRight,
    handleScrollLeft,
    handleNavigationArrows,
    holderRef,
    trailRef,
    fillSpace: usedItemsPerView - (numberOfSlides%usedItemsPerView)
  }
}

export default useSlider
